import dayJs, { Dayjs } from 'dayjs';
import { arrayLiteralToCamelCase } from 'pos-shared/utils/textHelpers.ts';
import { z } from 'zod';

import { DayJsSchema, NameValueSchema } from './commonSchema.ts';
import {
  createPaginatedResponseSchema,
  PagedRequestSchema,
  SearchRequestSchema,
} from './pagingSchema.ts';
import { PriceSchema, UnitSellPricingSchema } from './pricingSchema.ts';
import {
  TirePositionEnum,
  TreadDepthMeasurementEnum,
} from './vehicleSchema.ts';
import { WarrantySchema } from './warrantiesSchema.ts';

export const StockTakeProfileScheduleEnum = z.enum([
  'once',
  'daily',
  'quarterly',
  'annually',
]);
export const StockTakeProfileTypeEnum = z.enum(['Full', 'ByProductGroup']);

export const StockQuarantineStatusEnum = z.enum([
  'InQuarantine',
  'SentToStock',
  'ReturnedToSupplier',
  'WrittenOff',
  'Scrapped',
]);
const StockTakeProfileGroupNameMapper = z.any().transform((v) => {
  if (!v) return 'Test name';
  const parsed = z.string().safeParse(v);
  if (!parsed.success) return 'Test name';
  return parsed.data;
});

export const StockIdSchema = z.object({
  stockId: z.uuid(),
});

export const SearchStockUnitSchema = z.object({
  stockUnitId: z.uuid(),
  stockId: z.uuid().nullish(),
  stockDescription: z.string().nullish(),
  stockSku: z.string().nullish(),
  supplierId: z.uuid().nullish(),
  supplierName: z.string().nullish(),
  partNumber: z.string().nullish(),
  originatorType: z.string().nullish(),
  purchaseOrder: z.string().nullish(),
  deliveryNote: z.string().nullish(),
  dateDelivered: z.string().nullish(),
  expectedDelivery: z.string().nullish(),
  receivedBy: z.string().nullish(),
  packUnitCurrentQuantity: z.number().nullish(),
  packUnitMeasurement: z.string().nullish(),
});

export const StockTakeProfileSchema = z.object({
  stockTakeProfileId: z.uuid(),
  schedule: z.string(), // static config value
  firstDate: z.coerce.date<Date>().nullish(),
  stockTakeProfileGroupId: z.uuid().nullish(),
  stockTakeProfileGroupName: StockTakeProfileGroupNameMapper.nullish(),
  stockTakeProfileCycleDay: z.string().nullish(), // static config value
  lastExecutionDate: z.coerce.date<Date>().nullish(),
  nextExecutionDate: z.coerce.date<Date>().nullish(),
  type: z.string(),
  typeIds: z.array(z.uuid()),
  applyTo: z.object({
    allServiceLocations: z.boolean(),
    serviceLocationIds: z.array(z.uuid()).optional(),
  }),
  description: z.string().min(1).optional(),
  isActive: z.boolean(),
  isDeleted: z.boolean(),
});

export const CreateStockTakeProfileSchema = z
  .object({
    serviceLocationIds: z.uuid().array().min(1),
    typeIds: z.array(z.uuid()).optional(),
  })
  .extend(
    StockTakeProfileSchema.pick({
      description: true,
      type: true,
      stockTakeProfileGroupId: true,
      schedule: true,
      stockTakeProfileCycleDay: true,
      firstDate: true,
    }).shape
  );

export const UpdateStockTakeProfileTypeIdsRequestSchema = z.object({
  stockTakeProfileId: z.uuid(),
  typeIds: z.array(z.uuid()),
});

export const EditStockTakeProfileMutationSchema =
  CreateStockTakeProfileSchema.extend({
    stockTakeProfileId: z.uuid(),
  });

export const StockTakeProfileCreateResponseSchema = StockTakeProfileSchema.pick(
  {
    stockTakeProfileId: true,
  }
);

export const CreateStockFormSchema = z.object({
  dueDate: z.instanceof(dayJs as unknown as typeof Dayjs),
  serviceLocationId: z.uuid(),
  stockTakeProfileGroupId: z.uuid(),
});

export const GetStockTakeProfilesRequestSchema = PagedRequestSchema.extend({
  searchText: z.string().optional(),
  serviceLocationIds: z.uuid().optional(),
  executionStart: z.coerce.date<Date>().optional(),
  executionEnd: z.coerce.date<Date>().optional(),
  type: StockTakeProfileTypeEnum.optional(),
});
export const GetStockTakeProfileGroupsRequestSchema = PagedRequestSchema;

export const GetStockTakeProfilesResponseSchema = createPaginatedResponseSchema(
  StockTakeProfileSchema
);

export const UpdateStockTakeProfileResponseSchema = z.object({
  description: z.string(),
  stockTakeProfileGroupId: z.string(),
  schedule: z.string().nullish(),
  cycleDay: z.string().nullish(),
  isActive: z.boolean(),
});

export const StockUnitSchema = z.object({
  stockUnitId: z.uuid(),
  stockId: z.uuid(),
  serviceLocationId: z.uuid().optional(),
  serviceLocationName: z.string().optional(),
  originatorType: z.string().optional(),
  source: z
    .object({
      supplierId: z.uuid(),
      supplierName: z.string(),
      partNumber: z.string().optional(),
      po: z.string().optional(),
      importEventId: z.string(),
      unitPrice: PriceSchema,
    })
    .optional(),
  delivery: z
    .object({
      deliveryNote: z.string(),
      dateDelivered: z.coerce.date<Date>().optional(),
      expectedDelivery: z.coerce.date<Date>().optional(),
    })
    .optional(),
  globalStatus: z.string().optional(),
  currentStatus: z.string().optional(),
  packUnitCurrentQuantity: z.number().optional(),
  dateAdded: z.coerce.date<Date>().optional(),
});

export const StockMeasurementsSchema = z.object({
  treadDepth: z.number().nullish(),
  treadDepthUnitOfMeasure: TreadDepthMeasurementEnum.optional(),
  warrantyTime: z.number().nullish(),
  warrantiedMiles: z.number().nullish(),
  position: TirePositionEnum.optional(),
});

export const AddStockMeasurementsSchema = StockMeasurementsSchema.extend({
  stockId: z.uuid(),
});

export const StockSchema = z.object({
  id: z.uuid(),
  sku: z.string(),
  productName: z.string().optional(),
  description: z.string().optional(),
  categoryId: z.uuid().optional(),
  identifierPrimary: z.string().optional(),
  identifierSecondary: z.string().optional(),
  retailPricingExTax: z.number(),
  retailPricingIncTax: z.number(),
  retailPricingTaxAmount: z.number(),
  sellOnPos: z.boolean(),
  isStockTracked: z.boolean(),
  fulfilmentTime: z.number().optional(),
  status: z.string().nullish(),
  oneSourceRef: z.string().optional(),
  warranty: z.array(WarrantySchema).optional(),
  itemTypeId: z.number().or(z.string()),
  bucketCode: z.string().optional(),
  bucketDescription: z.string().optional(),
});

export const StockFormSchema = StockSchema.omit({
  id: true,
  sku: true,
  description: true,
  retailPricingExTax: true,
  retailPricingIncTax: true,
  retailPricingTaxAmount: true,
}).extend({
  sku: z.string().min(1),
  description: z.string().min(1),
  retailPricingExTax: z.number().optional(),
  retailPricingIncTax: z.number().optional(),
  retailPricingTaxAmount: z.number().optional(),
  oneSourceRef: z.string().optional(),
  measurements: StockMeasurementsSchema.optional(),
});

export const CreateStockSchema = StockSchema.omit({ id: true });

export const SearchStockRequestSchema = PagedRequestSchema.extend({
  searchText: z.coerce.string<string>().optional(),
  sellOnPos: z.boolean().optional(),
  categoryId: z.uuid().optional(),
  status: z.string().optional(),
});

export const SearchStockResponseSchema =
  createPaginatedResponseSchema(StockSchema);

export const SearchStockResponseBeSchema = createPaginatedResponseSchema(
  StockSchema.omit({ status: true, sku: true }).extend({
    currentStatus: z.string(),
    sku: z.string().nullish(),
  })
);

export const StockTakeItemSchema = z.object({
  stockTakeItemId: z.uuid(),
  partNumber: z.number().or(z.string()).nullish(),
  supplier: z.string().nullish(),
  productGroup: z.string().nullish(),
  description: z.string().nullish(),
  quantityExpected: z.number().optional(),
  quantityCounted: z.number().optional(),
  quantityDelta: z.number().optional(),
  quantityChecked: z.number().optional(),
  checked: z.boolean(),
});

export const StockTakeStatusEnum = z.enum(['Open', 'Complete', 'ToBeComplete']);

export const StockTakeSchema = z.object({
  stockTakeId: z.uuid(),
  serviceLocationId: z.number().or(z.string()),
  serviceLocationName: z.string().nullish(),
  dueDate: z.coerce.date<Date>().nullish(),
  openedStatus: z.boolean(),
  dateLastOpened: z.coerce.date<Date>().optional(),
  dateLastClosed: z.coerce.date<Date>().optional(),
  completed: z.boolean(),
  completedBy: z.string().optional(),
  completedDate: z.coerce.date<Date>().optional(),
  type: z.string().optional(),
  isActive: z.boolean(),
  items: z.array(StockTakeItemSchema).nullable(),
  status: StockTakeStatusEnum.nullish(),
});

export const StockTakeSearchResponseSchema =
  createPaginatedResponseSchema(StockTakeSchema);

export const StockReturnsRequestSchema = z
  .object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    serviceLocationId: z.uuid().optional(),
    status: z.string().optional(),
    supplierId: z.uuid().optional(),
  })
  .extend(PagedRequestSchema.shape);

export type StockReturnsRequestModel = z.infer<
  typeof StockReturnsRequestSchema
>;

export const StockTakeSearchRequestSchema = z
  .object({
    dueDate: z.coerce.date<Date>().optional(),
    serviceLocationId: z.uuid().optional(),
    completedBy: z.string().optional(),
    type: z.string().optional(),
    completedDate: z.coerce.date<Date>().optional(),
    status: StockTakeStatusEnum.optional(),
  })
  .extend(PagedRequestSchema.shape);

export const StockUnitSearchRequestSchema = SearchRequestSchema.pick({
  pageNumber: true,
  resultsPerPage: true,
  searchText: true,
});

export const StockUnitSearchSchema = SearchStockUnitSchema.extend({
  stockId: z.uuid(),
  stockDescription: z.string(),
  stockSku: z.string(),
  supplierId: z.uuid(),
  supplierName: z.string(),
  partNumber: z.string(),
  deliveryNote: z.string(),
  dateDelivered: z.coerce.date<Date>(),
});

export const StockUnitSearchResponseSchema = z.object({
  totalPages: z.number(),
  totalResults: z.number(),
  results: z.array(StockUnitSearchSchema),
});

export const CreateStockTakeRequestSchema = z.object({
  stockTakeProfileId: z.uuid(),
  serviceLocationId: z.uuid(),
  serviceLocationName: z.string().nullish(),
  dueDate: z.iso.datetime(),
  openedStatus: z.boolean().optional(),
});

export const CreateStockTakeFormSchema = CreateStockTakeRequestSchema.omit({
  dueDate: true,
}).extend({
  dueDate: z.instanceof(dayJs as unknown as typeof Dayjs),
});

export const StockTakeIdSchema = z.object({
  stockTakeId: z.uuid(),
});

export const AddStockTakeItemRequestSchema = z.object({
  stockTakeId: z.uuid(),
  stockId: z.uuid(),
  partNumber: z.string(),
  supplier: z.string(),
  supplierId: z.uuid().nullish(),
  productGroup: z.string().nullish(),
  description: z.string(),
  quantityExpected: z.number(),
  checked: z.boolean(),
});

export const StockTakeIdResponseSchema = z.object({
  stockTakeId: z.uuid(),
});

export const StockTransferResponseSchema = z.object({
  stockTransferId: z.uuid(),
});

export const StockTakeItemUpdateSchema = z.object({
  stockTakeId: z.uuid(),
  items: z.array(StockTakeItemSchema).nullish(),
});

export const StockTakeItemUpdateResponseSchema = z.object({
  stockTakeId: z.uuid(),
  updatedItemIds: z.array(z.uuid()).optional(),
});

export const StockTransferStatusEnum = z.enum([
  'Initiated',
  'InTransit',
  'Completed',
  'Cancelled',
]);

export const StockTransferSchema = z.object({
  id: z.uuid(),
  stockId: z.uuid(),
  description: z.string().min(1),
  destination: z.object({
    serviceLocationId: z.uuid(),
    name: z.string().nullable(),
    address: z.string().nullable(),
  }),
  source: z.object({
    serviceLocationId: z.uuid(),
    name: z.string().nullable(),
    address: z.string().nullable(),
  }),
  quantityToTransfer: z.number().optional(),
  reasonCode: z.string().min(1).optional(),
  received: z
    .object({
      quantityReceived: z.number().optional(),
      dateReceived: z.coerce.date<Date>().nullish(),
      receivedBy: z.uuid().nullish(),
      receivedByName: z.string().optional(),
    })
    .nullable()
    .optional(),
  status: z
    .object({
      id: z.uuid(),
      statusValue: z.string(),
      audit: z.object({
        createdBy: z.uuid().nullish(),
      }),
    })
    .nullish(),
  transferDate: z.coerce.date<Date>(),
  associatedToOrderJobItemPart: z
    .object({
      orderId: z.uuid().optional(),
      jobId: z.uuid().optional(),
      itemId: z.uuid().optional(),
      partId: z.uuid().optional(),
    })
    .nullable()
    .optional(),
  stockUnitIdsToTransfer: z.array(z.uuid()).optional(),
  createdBy: z.uuid().nullish(),
  createdByName: z.string().nullish(),
  stockSku: z.string().nullish(),
  stockDescription: z.string().nullish(),
});

export const CreateOrEditStockTransferSchema = StockTransferSchema.omit({
  id: true,
  transferDate: true,
}).extend({
  transferDate: z.string(),
});

export const CreateOrEditStockTransferFormSchema = StockTransferSchema.omit({
  associatedToOrderJobItemPart: true,
  transferDate: true,
  status: true,
  id: true,
  source: true,
  stockId: true,
  stockUnitIdsToTransfer: true,
}).extend({
  transferDate: z.instanceof(dayJs as unknown as typeof Dayjs),
});

export const StockTransferSearchRequestSchema = z
  .object({
    direction: z.string().optional(),
    directionServiceLocationId: z.uuid().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    reasonCode: z.string().optional(),
    sku: z.string().optional(),
    status: z.string().optional(),
  })
  .extend(PagedRequestSchema.shape);

export const StockTransferSearchResponseSchema =
  createPaginatedResponseSchema(StockTransferSchema);

export const ReceiveStockTransferSchema = z.object({
  stockTransferId: z.uuid(),
  data: z.object({
    quantityReceived: z.number(),
    dateReceived: z.coerce.date<Date>(),
    receivedBy: z.string(),
  }),
});

export const ReceiveStockTransferFormSchema = z.object({
  stockTransferId: z.uuid(),
  quantityReceived: z.number().min(1),
  dateReceived: z.coerce.date<Date>(),
  receivedBy: z.string(),
});

export const StockAdjustmentSchema = z.object({
  stockAdjustmentId: z.uuid(),
  serviceLocationId: z.uuid(),
  stockDescription: z.string().nullish(),
  sku: z.string().nullish(),
  dateCreated: z.coerce.date<Date>(),
  stockId: z.uuid(),
  quantityPre: z.number(),
  quantityPost: z.number(),
  adjustmentQuantity: z.number(),
  creationType: z.string(),
  reason: z.string().optional(),
  comments: z.string().optional(),
  resultOfStockTakeId: z.uuid().optional(),
  createdBy: z.string(),
  reference: z.string().nullish(),
});

export const StockAdjustmentsSearchRequestSchema = PagedRequestSchema.extend({
  serviceLocationId: z.uuid().optional(),
  sku: z.string().optional(),
  stockDescription: z.string().optional(),
  dateCreated: z.coerce.date<Date>().optional(),
  createdBy: z.string().optional(),
});

export const StockAdjustmentsSearchResponseSchema =
  createPaginatedResponseSchema(StockAdjustmentSchema);

export const CreateStockAdjustmentSchema = StockAdjustmentSchema.pick({
  serviceLocationId: true,
  stockId: true,
  quantityPost: true,
  creationType: true,
  resultOfStockTakeId: true,
}).partial({ resultOfStockTakeId: true });

export const StockAdjustmentFormSchema = CreateStockAdjustmentSchema.extend({
  adjustmentQuantity: z.number().min(-500).max(500),
  quantityPre: z.number(),
  reason: z.string(),
  comments: z.string().optional(),
});

export const StockAdjustmentResponseSchema = z.object({
  stockAdjustmentId: z.uuid(),
});

export const CreateStockReturnSchema = z.object({
  quantity: z.coerce
    .number<number>({
      error: (issue) => (issue.input === undefined ? undefined : 'Required'),
    })
    .min(1),
  returnDate: z.coerce.date<Date>(),
  returnReason: z.string(),
});

export const AddStockReturnRequestSchema = z.object({
  orderId: z.uuid(),
  partId: z.uuid(),
  quantity: z.int(),
  returnNoteReference: z.string().nullish(),
  toReturnDate: z.string().nullish(),
  reasonForReturn: z.string().nullish(),
});

export const StockReturnResponseSchema = z.object({
  ids: z.uuid().array(),
});

export const StockReturnIdSchema = z.object({
  stockReturnId: z.uuid(),
});

export const StockDeliveriesRequestSchema = z.object({
  serviceLocationId: z.uuid().optional(),
  startDate: z.coerce.date<Date>().optional(),
  endDate: z.coerce.date<Date>().optional(),
  supplierId: z.uuid().optional(),
  purchaseOrder: z.string().optional(),
  orderRef: z.string().optional(),
});

export const StockLevelQuantitySchema = z.object({
  status: z.string(),
  quantity: z.number(),
});

export const StockDeliverySchema = StockUnitSchema.pick({
  stockUnitId: true,
  stockId: true,
  originatorType: true,
}).extend({
  stockSku: z.string().nullish(),
  stockDescription: z.string().nullish(),
  supplierId: z.uuid().nullish(),
  supplierName: z.string().nullish(),
  partNumber: z.string().nullish(),
  purchaseOrder: z.string().nullish(),
  deliveryNote: z.string().nullish(),
  dateDelivered: z.coerce.date<Date>().nullish(),
  receivedBy: z.string().nullish(),
  expectedDelivery: z.coerce.date<Date>().nullish(),
  orderId: z.uuid().nullish(),
  orderRef: z.string().nullish(),
});

export const StockDeliveriesResponseSchema = z.object({
  stockUnits: z.array(StockDeliverySchema),
});

export const ReceiveStockUnitSchema = z.object({
  serviceLocationId: z.uuid(),
  stockUnitId: z.uuid(),
  receivedBy: z.string(),
  deliveryNote: z.string(),
  dateDelivered: z.coerce.date<Date>(),
  orderRef: z.string().nullish(),
});

export const ReceiveStockSchema = z.object({
  deliveries: z.array(ReceiveStockUnitSchema),
});

//Stock Take Profile Models

export const StockUseOrderPartsDetailsSchema = z.object({
  jobId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
  stockSku: z.string(),
  quantity: z.number(),
  partNumber: z.string(),
  stockMovementType: z.string(),
  stockUnitStatus: z.string(),
  supplierId: z.uuid(),
  supplierName: z.string(),
  originatorType: z.string(),
  purchaseOrderId: z.string(),
  unitBuyPrice: PriceSchema,
  expectedDeliveryDate: z.string(),
});

export const CreateUseOrderPartsFromStockSchema = z.object({
  orderId: z.uuid(),
  serviceLocationId: z.uuid(),
});

export const StockUseOrderPartsRequestSchema = z.object({
  orderId: z.uuid(),
  serviceLocationId: z.uuid(),
  partDetails: StockUseOrderPartsDetailsSchema.array(),
});

export const StockUseOrderPartsResponseSchema = z.object({
  allocatedFromInternalProducts: z.string().array(),
  orderedFromSupplier: z.string().array(),
});

export const FECreateStockBaseSchema = z.object({
  stockSku: z.string(),
  stockDescription: z.string(),
  serviceLocationId: z.string(),
  lowHoldingAlert: z.coerce.number<number>().min(0),
  maxHolding: z.coerce.number<number>().min(0),
  minHolding: z.coerce.number<number>().min(0),
  stockId: z.string(),
});

export const StockLevelOtherQuantitiesSchema = z.object({
  status: z.string(),
  quantity: z.number(),
});

export const StockLevelPriceSchema = z.object({
  exTax: z.number(),
  incTax: z.number(),
  taxAmount: z.number(),
});

export const StockLevelSearchSchema = z.object({
  stockId: z.uuid(),
  serviceLocationId: z.uuid(),
  sku: z.string().nullish(),
  description: z.string().nullish(),
  quantityInStock: z.number(),
  price: StockLevelPriceSchema,
  fulfilmentTime: z.number().nullish(),
  lowHoldingAlert: z.boolean(),
  sellOnPos: z.boolean().nullish(),
  otherQuantities: StockLevelOtherQuantitiesSchema.array(),
  warranty: WarrantySchema.array().nullish(),
  categoryId: z.uuid().nullish(),
});

export const StockLevelGetSchema = z.object({
  stockLevelId: z.uuid(),
  quantity: z.int(),
  stockId: z.uuid(),
  serviceLocationId: z.uuid(),
});

export const StockLevelSearchRequestSchema = PagedRequestSchema.extend({
  serviceLocationId: z.string(),
  sku: z.coerce.string<string>().optional(),
  status: z.string().optional(),
  sellOnPos: z.boolean().optional(),
  categoryId: z.uuid().optional(),
});

export const StockInternalProductSearchRequestSchema =
  PagedRequestSchema.extend({
    serviceLocationId: z.uuid(),
    categoryId: z.uuid().optional(),
    sellOnPos: z.boolean().optional(),
    searchText: z.string().optional(),
    status: z.string().optional(),
    useProfilePrice: z.boolean().optional(),
  });

export const StockInternalProductSchema = z.object({
  id: z.uuid(),
  sku: z.string().nullish(),
  description: z.string().nullish(),
  identifierPrimary: z.string().nullish(),
  identifierSecondary: z.string().nullish(),
  currentStatus: z.string().nullish(),
  sellOnPos: z.boolean().nullish(),
  isStockTracked: z.boolean().nullish(),
  retailPricingExTax: z.number().nullish(),
  retailPricingIncTax: z.number().nullish(),
  retailPricingTaxAmount: z.number().nullish(),
  fulfilmentTime: z.number().nullish(),
  level: z.array(
    z.object({
      status: z.string(),
      quantity: z.number(),
    })
  ),
  oneSourceRef: z.string().optional(),
  lowHoldingAlert: z.boolean(),
  warranty: WarrantySchema.array(),
  categoryId: z.uuid().nullish(),
});

export const StockInternalProductResponseSchema = createPaginatedResponseSchema(
  StockInternalProductSchema
);

export const StockLevelSearchResponseSchema = createPaginatedResponseSchema(
  StockLevelSearchSchema
);

export const StockLevelByIdSchema = z.object({
  stockId: z.uuid(),
  serviceLocationId: z.uuid(),
});

export const ReceiveStockFormSchema = z.object({
  note: z.string(),
});

export const StockCategoryBaseSchema = z.object({
  categoryId: z.uuid(),
  name: z.string(),
  parentCategoryId: z.uuid().optional(),
});

// According to Zod docs, this is how you handle a recursive type and schema
export type StockCategoryModel = z.infer<typeof StockCategoryBaseSchema> & {
  subCategories?: StockCategoryModel[];
};

export const StockCategorySchema: z.ZodType<StockCategoryModel> =
  StockCategoryBaseSchema.extend({
    subCategories: z.lazy(() => StockCategorySchema.array()),
  });

export const SearchStockCategoryRequestSchema = z.object({
  categoryId: z.uuid().optional(),
  name: z.string(),
});

export const GetStockCategoryHierarchyResponseSchema = z.object({
  categoryId: z.uuid(),
  name: z.string().optional(),
  subCategories: StockCategorySchema.array(),
});

export const CreateStockCategoryRequestSchema = StockCategoryBaseSchema.omit({
  categoryId: true,
});

export const UpdateStockCategoryRequestSchema = z.object({
  categoryId: z.uuid(),
  newName: z.string().optional(),
  newParentId: z.uuid().optional(),
});

export const StockCategoryFormSchema = z.object({
  name: z.string().min(1),
  parentCategoryId: z.uuid().optional(),
});

export const CreateJobFromStockFormItemSchema = z.object({
  stockId: z.uuid(),
  sku: z.string(),
  description: z.string(),
  quantity: z.number().min(1),
  quantityInStock: z.number(),
  fulfilmentTime: z.number().nullish(),
  price: StockLevelPriceSchema,
  oneSourceRef: z.string().optional(),
  warranty: WarrantySchema.array().nullish(),
  measurements: StockMeasurementsSchema.nullish(),
  category: z.string().nullish(),
});

export const CreateJobFromStockFormSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string(),
  fulfilmentBaseLocation: z.uuid(),
  items: CreateJobFromStockFormItemSchema.array().min(1),
});

export const PartSourceEnum = z.enum(['INT', 'Epicor', 'TC']);

export const CreateJobFromInternalStockJobInfoSchema = z.object({
  orderNumber: z.string(),
  creationSource: z.enum([
    'CustomerRequest',
    'OtherJobDoingIdentified',
    'SystemDataIdentified',
  ]),
  fulfilmentBaseLocation: z.uuid(),
  fulfilmentType: z.string().nullish(),
  jobType: z.string().nullish(),
  jobDescription: z.string(),
  category: z.string().nullish(),
});

export const CreateJobFromInternalStockItemInfoSchema = z.object({
  itemDescription: z.string(),
  schedulingConstraints: z.object({
    totalDuration: z.number(),
    assetConstraints: z.string().nullish(),
  }),
  reasonForAddition: z
    .object({
      description: z.string().nullish(),
      reasonCode: z.string().nullish(),
    })
    .nullish(),
  measurements: StockMeasurementsSchema.nullish(),
});

export const CreateJobFromInternalStockPartInfoSchema = z.object({
  stockId: z.uuid().optional(),
  unitQuantity: z.number(),
  description: z.string(),
  partNumber: z.string().nullish(),
  sku: z.string(),
  supplier: z
    .object({
      supplierId: z.uuid(),
      stockCode: z.string().nullish(),
      leadTime: z.number().nullish(),
    })
    .nullish(),
  productTaxCode: z.string().nullish(),
  unitBuyPrice: PriceSchema,
  unitSellPrice: UnitSellPricingSchema,
  partSource: PartSourceEnum,
});

export const CreateJobFromInternalStockSchema = z.object({
  jobId: z.uuid().optional(),
  jobInfo: CreateJobFromInternalStockJobInfoSchema,
  itemInfo: CreateJobFromInternalStockItemInfoSchema,
  partInfo: CreateJobFromInternalStockPartInfoSchema,
});

export const CreateJobFromInternalStockRequestSchema = z.object({
  orderId: z.uuid(),
  request: CreateJobFromInternalStockSchema.array(),
});

export const CreateJobFromInternalStockResponseSchema = z.object({
  response: z
    .object({
      orderId: z.uuid(),
      jobId: z.uuid(),
      itemId: z.uuid(),
      partId: z.uuid(),
    })
    .array(),
});

export const StockProfileSupplierSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  costPriceExTax: z.number().nullish(),
  costPriceIncTax: z.number().nullish(),
  costPriceTax: z.number().nullish(),
  isPrimary: z.boolean(),
});

export const StockProfileReplenishEnum = z.enum(['None', 'Live', 'Bulk']);

export const StockProfileSchema = z.object({
  id: z.uuid(),
  stockId: z.uuid(),
  name: z.string().nullish(),
  stockSku: z.string().nullish(),
  stockDescription: z.string().nullish(),
  minHolding: z.number().nullish(),
  maxHolding: z.number().nullish(),
  lowHoldingAlert: z.number().nullish(),
  lowHoldingAlertAction: z.string().nullish(),
  autoReplenish: z.boolean().nullish(),
  autoReplenishMethod: StockProfileReplenishEnum.nullish(),
  autoReturnMethod: StockProfileReplenishEnum.nullish(),
  salePriceExTax: z.number().nullish(),
  salePriceIncTax: z.number().nullish(),
  salePriceTax: z.number().nullish(),
  suppliers: StockProfileSupplierSchema.array(),
  serviceLocationIds: z.uuid().array(),
});

export const SearchStockProfileRequestSchema = z
  .object({
    stockId: z.uuid().optional(),
    supplierId: z.uuid().optional(),
    serviceLocationId: z.uuid().optional(),
  })
  .extend(PagedRequestSchema.shape);

export const SearchStockProfileResponseSchema =
  createPaginatedResponseSchema(StockProfileSchema);

export const StockProfileFormSchema = StockProfileSchema.omit({
  id: true,
  name: true,
  stockSku: true,
  minHolding: true,
  maxHolding: true,
  lowHoldingAlert: true,
  lowHoldingAlertAction: true,
  autoReplenishMethod: true,
  autoReturnMethod: true,
  suppliers: true,
  serviceLocationIds: true,
}).extend({
  name: z.string().min(1),
  stockSku: z.string(),
  minHolding: z.number().nonnegative().nullish(),
  maxHolding: z.number().nonnegative().nullish(),
  lowHoldingAlert: z.number().nonnegative().nullish(),
  autoReplenishMethod: StockProfileReplenishEnum,
  autoReturnMethod: StockProfileReplenishEnum,
  suppliers: StockProfileSupplierSchema.array().min(1),
  serviceLocationIds: z.array(z.uuid()).min(1),
});

export const CreateStockProfileSchema = StockProfileSchema.omit({ id: true });

export const GetStockAssociatedOrdersRequestSchema = PagedRequestSchema.extend({
  stockId: z.uuid(),
});

export const GetStockLevelTotalsByLocationIdRequestSchema = z.object({
  serviceLocationId: z.uuid().nullish(),
});

export const StockAssociatedOrderSchema = z.object({
  orderId: z.uuid().nullish(),
  orderNumber: z.string().nullish(),
});

export const GetStockAssociatedOrdersResponseSchema =
  createPaginatedResponseSchema(StockAssociatedOrderSchema);

export const GetStockLevelTotalsByLocationIdResponseSchema = z.object({
  totals: z.array(StockLevelQuantitySchema),
});

export const StockQuarantineActionEnum = z.enum([
  'SendToStock',
  'ReturnToSupplier',
]);

export const StockQuarnatineActionCamelEnum = z.enum(
  arrayLiteralToCamelCase(StockQuarantineActionEnum.options)
);

export const StockQuarantineItemStatusEnum = z.enum(['open', 'closed']);

export const StockQuarantineSchema = z.object({
  id: z.uuid(),
  stockId: z.uuid(),
  name: z.string().nullish(),
  sku: z.string().optional(),
  description: z.string().optional(),
  quantity: z.number().optional(),
  purchaseOrderReference: z.string().nullish(),
  orderReference: z.string().nullish(),
  serviceLocationId: z.uuid(),
  entryDate: z.coerce.date<Date>().nullish(),
  reasonForEntry: z.string().optional(),
  exitDate: z.coerce.date<Date>().nullish(),
  reasonForExit: z.string().optional(),
  exitAction: StockQuarantineActionEnum.nullish(),
  quarantineStatus: StockQuarantineStatusEnum.optional(),
  isClosed: z.boolean().optional(),
  returnReasonCode: z.string().nullish(),
});

export const StockQuarantineFormSchema = StockQuarantineSchema.omit({
  id: true,
  quantity: true,
  entryDate: true,
  exitDate: true,
  quarantineStatus: true,
  isClosed: true,
}).extend({
  quantity: z.number().min(1),
  entryDate: z.coerce.date<Date>().or(DayJsSchema).nullish(),
  exitDate: z.coerce.date<Date>().or(DayJsSchema).nullish(),
});

export const StockQuarantineSearchResponseSchema =
  createPaginatedResponseSchema(StockQuarantineSchema);

export const StockQuarantineSearchRequestSchema = z
  .object({
    serviceLocationId: z.uuid().optional(),
    stockId: z.uuid().optional(),
    sku: z.string().optional(),
    description: z.string().optional(),
    orderReference: z.string().optional(),
    isOpen: z.boolean().optional(),
    isClosed: z.boolean().optional(),
    entryDate: z.coerce.date<Date>().optional(),
    exitDate: z.coerce.date<Date>().optional(),
  })
  .extend(PagedRequestSchema.shape);

export const SearchMeasurementsBySkuRequestSchema = z.object({
  sku: z.string().min(1),
});

export const SearchMeasurementsBySkuResponseSchema =
  StockMeasurementsSchema.omit({
    position: true,
  });

export const GetByServiceLocationOrStockRequestSchema = z.object({
  serviceLocationId: z.uuid(),
  stockId: z.uuid().optional(),
  useServiceLocationRadius: z.boolean().optional(),
  pageNumber: z.number().optional(),
  resultsPerPage: z.number().optional(),
});

const GetByServiceLocationOrStockSchema = z.object({
  stockUnitId: z.uuid(),
  stockId: z.uuid().nullish(),
  serviceLocationId: z.uuid().nullish(),
  status: z.string().nullish(),
  stockDescription: z.string().nullish(),
  stockSku: z.string().nullish(),
  supplierId: z.uuid().nullish(),
  supplierName: z.string().nullish(),
  partNumber: z.string().nullish(),
  originatorType: z.string().nullish(),
  purchaseOrder: z.string().nullish(),
  deliveryNote: z.string().nullish(),
  dateDelivered: z.string().nullish(),
  expectedDelivery: z.string().nullish(),
  receivedBy: z.string().nullish(),
  packUnitCurrentQuantity: z.number().nullish(),
  packUnitMeasurement: z.string().nullish(),
});

export const GetByServiceLocationOrStockResponseSchema =
  createPaginatedResponseSchema(GetByServiceLocationOrStockSchema);

export const StockTakeProfileCycleDaysTypeSchema = z.object({
  stockTakeProfileCycleDays: z.array(NameValueSchema).nullish(),
});

export const StockReturnStatusEnum = z
  .enum(['Awaiting', 'Completed', 'Cancelled'])
  .nullish();

export const StockReturnSchema = z.object({
  stockReturnId: z.uuid(),
  returnNoteReference: z.string().nullish(),
  serviceLocationId: z.uuid(),
  product: z
    .object({
      productId: z.uuid().nullish(),
      partNumber: z.string().nullish(),
      description: z.string().nullish(),
    })
    .nullish(),
  quantity: z.int(),
  sourceSupplierName: z.string().nullish(),
  returnToSupplierName: z.string().nullish(),
  returnToSupplierAddress: z.string().nullish(),
  toReturnDate: z.string().nullish(),
  reasonForReturn: z.string().nullish(),
  reasonForReturnCode: z.string().nullish(),
  returned: z.boolean(),
  returnedDate: z.string().nullish(),
  supplierId: z.uuid(),
  stockId: z.uuid().nullish(),
  createdById: z.uuid(),
  createdBy: z.string().nullish(),
  dateCreated: z.string(),
  stockUnits: z.array(StockUnitSchema.partial({ stockId: true })).nullish(),
  statuses: z
    .array(
      z.object({
        status: z.enum(['Awaiting', 'Completed', 'Cancelled']).nullish(),
      })
    )
    .nullish(),
  sku: z.string().nullish(),
  status: StockReturnStatusEnum,
});

export const StockReturnFormSchema = z.object({
  serviceLocationId: z.uuid(),
  stockId: z.uuid(),
  purchaseOrderId: z.uuid(),
  quantity: z.number().min(1).max(10),
  reasonForReturn: z.string().nullish(),
  toReturnDate: z.coerce.date(),
});

export const GetStockReturnResponseSchema =
  createPaginatedResponseSchema(StockReturnSchema);

export const StockTakeItemIdSchema = z.object({
  stockTakeItemId: z.uuid(),
});

export const StockReturnIdsSchema = z.object({
  ids: z.uuid().array(),
});

export const ReplenishStockRequestSchema = z.object({
  profileIds: z.uuid().array(),
});

export type StockIdModel = z.infer<typeof StockIdSchema>;
export type SearchStockCategoryRequestModel = z.infer<
  typeof SearchStockCategoryRequestSchema
>;
export type GetStockCategoryHierarchyResponseModel = z.infer<
  typeof GetStockCategoryHierarchyResponseSchema
>;
export type CreateStockCategoryRequestModel = z.infer<
  typeof CreateStockCategoryRequestSchema
>;
export type UpdateStockCategoryRequestModel = z.infer<
  typeof UpdateStockCategoryRequestSchema
>;
export type StockCategoryFormModel = z.infer<typeof StockCategoryFormSchema>;

export const SearchStockUnitsRequestSchema = z.object({
  serviceLocationId: z.uuid(),
  stockId: z.uuid().optional(),
});

export type SearchStockUnitsRequestModel = z.infer<
  typeof SearchStockUnitsRequestSchema
>;

export type StockTakeProfile = z.infer<typeof StockTakeProfileSchema>;
export type GetStockTakeProfileGroupsRequestModel = z.infer<
  typeof GetStockTakeProfileGroupsRequestSchema
>;
export type GetStockTakeProfilesRequestModel = z.infer<
  typeof GetStockTakeProfilesRequestSchema
>;
export type GetStockTakeProfilesResponseModel = z.infer<
  typeof GetStockTakeProfilesResponseSchema
>;
export type CreateStockTakeProfileModel = z.infer<
  typeof CreateStockTakeProfileSchema
>;
export type UpdateStockTakeProfileTypeIdsRequestModel = z.infer<
  typeof UpdateStockTakeProfileTypeIdsRequestSchema
>;

export type EditStockTakeProfileMutationModel = z.infer<
  typeof EditStockTakeProfileMutationSchema
>;
export type StockTakeProfileCreateResponse = z.infer<
  typeof StockTakeProfileCreateResponseSchema
>;
export type StockTakeType = z.infer<typeof StockTakeProfileTypeEnum>;

export type StockUnitSearchModel = z.infer<typeof StockUnitSearchSchema>;

export type StockUnitSearchRequestModel = z.infer<
  typeof StockUnitSearchRequestSchema
>;

export type StockUnitSearchResponseModel = z.infer<
  typeof StockUnitSearchResponseSchema
>;

export type AddStockTakeItemRequestModel = z.infer<
  typeof AddStockTakeItemRequestSchema
>;

export type ReceiveStockFormModel = z.infer<typeof ReceiveStockFormSchema>;

export type UpdateStockTakeProfileResponseModel = z.infer<
  typeof UpdateStockTakeProfileResponseSchema
>;

export type StockUnitModel = z.infer<typeof StockUnitSchema>;

// Stock Take Models
export type StockTakeModel = z.infer<typeof StockTakeSchema>;
export type StockTakeStatusType = z.infer<typeof StockTakeStatusEnum>;
export type StockTakeIdReponseModel = z.infer<typeof StockTakeIdResponseSchema>;
export type StockTransferResponseModel = z.infer<
  typeof StockTransferResponseSchema
>;
export type CreateStockTakeRequestModel = z.infer<
  typeof CreateStockTakeRequestSchema
>;
export type CreateStockTakeFormModel = z.infer<
  typeof CreateStockTakeFormSchema
>;
export type StockTakeSearchRequestModel = z.infer<
  typeof StockTakeSearchRequestSchema
>;
export type StockTakeSearchResponseModel = z.infer<
  typeof StockTakeSearchResponseSchema
>;
export type StockTakeItemUpdateModel = z.infer<
  typeof StockTakeItemUpdateSchema
>;
export type StockTakeItemUpdateResponseModel = z.infer<
  typeof StockTakeItemUpdateResponseSchema
>;

// Stock Transfer

export type StockTransferModel = z.infer<typeof StockTransferSchema>;
export type StockTransferSearchRequestModel = z.infer<
  typeof StockTransferSearchRequestSchema
>;
export type StockTransferSearchResponseModel = z.infer<
  typeof StockTransferSearchResponseSchema
>;
export type CreateOrEditStockTransferModel = z.infer<
  typeof CreateOrEditStockTransferSchema
>;
export type CreateOrEditStockTransferFormModel = z.infer<
  typeof CreateOrEditStockTransferFormSchema
>;

export type ReceiveStockTransferModel = z.infer<
  typeof ReceiveStockTransferSchema
>;

export type ReceiveStockTransferFormModel = z.infer<
  typeof ReceiveStockTransferFormSchema
>;

// Stock Adjustment
export type StockAdjustmentModel = z.infer<typeof StockAdjustmentSchema>;
export type StockAdjustmentsSearchResponseModel = z.infer<
  typeof StockAdjustmentsSearchResponseSchema
>;
export type StockAdjustmentsSearchRequestModel = z.infer<
  typeof StockAdjustmentsSearchRequestSchema
>;
export type StockAdjustmentFormModel = z.infer<
  typeof StockAdjustmentFormSchema
>;
export type CreateStockAdjustmentModel = z.infer<
  typeof CreateStockAdjustmentSchema
>;
export type StockAdjustmentResponseModel = z.infer<
  typeof StockAdjustmentResponseSchema
>;
export type StockReturnModel = z.infer<typeof StockReturnSchema>;
export type CreateStockReturnType = z.infer<typeof CreateStockReturnSchema>;
export type AddStockReturnRequestModel = z.infer<
  typeof AddStockReturnRequestSchema
>;
export type StockReturnResponseModel = z.infer<
  typeof StockReturnResponseSchema
>;

export type StockDeliveriesRequestModel = z.infer<
  typeof StockDeliveriesRequestSchema
>;
export type StockDeliveryModel = z.infer<typeof StockDeliverySchema>;
export type StockDeliveriesResponseModel = z.infer<
  typeof StockDeliveriesResponseSchema
>;
export type ReplenishStockRequestModel = z.infer<
  typeof ReplenishStockRequestSchema
>;

export type ReceiveStockModelUnit = z.infer<typeof ReceiveStockUnitSchema>;
export type ReceiveStockModel = z.infer<typeof ReceiveStockSchema>;
export type StockModel = z.infer<typeof StockSchema>;
export type StockFormModel = z.infer<typeof StockFormSchema>;
export type CreateStockModel = z.infer<typeof CreateStockSchema>;
export type SearchStockRequestModel = z.infer<typeof SearchStockRequestSchema>;
export type SearchStockResponseModel = z.infer<
  typeof SearchStockResponseSchema
>;
export type SearchStockResponseBeModel = z.infer<
  typeof SearchStockResponseBeSchema
>;

//Stock Profile Models

export type StockProfileModel = z.infer<typeof StockProfileSchema>;
export type SearchStockProfileRequestModel = z.infer<
  typeof SearchStockProfileRequestSchema
>;
export type SearchStockProfileResponseModel = z.infer<
  typeof SearchStockProfileResponseSchema
>;
export type CreateStockProfileModel = z.infer<typeof CreateStockProfileSchema>;
export type StockProfileSupplierModel = z.infer<
  typeof StockProfileSupplierSchema
>;
export type StockProfileFormModel = z.infer<typeof StockProfileFormSchema>;

//Order Parts from Stock
export type StockUseOrderPartsDetailsModel = z.infer<
  typeof StockUseOrderPartsDetailsSchema
>;
export type StockUseOrderPartsRequestModel = z.infer<
  typeof StockUseOrderPartsRequestSchema
>;
export type StockUseOrderPartsResponseModel = z.infer<
  typeof StockUseOrderPartsResponseSchema
>;

//Stock Quarantine
export type StockQuarantineModel = z.infer<typeof StockQuarantineSchema>;

export const ReturnReasonCodesConfigSchema = z.object({
  isEnabled: z.boolean(),
  reasonCodes: z.array(z.string()),
});
export type ReturnReasonCodesConfigModel = z.infer<
  typeof ReturnReasonCodesConfigSchema
>;

export const SupplierReturnFormSchema = z.object({
  returnReasonCode: z.string().min(1),
});
export type SupplierReturnFormModel = z.infer<typeof SupplierReturnFormSchema>;
export type StockQuarantineSearchRequestModel = z.infer<
  typeof StockQuarantineSearchRequestSchema
>;
export type StockQuarantineSearchResponseModel = z.infer<
  typeof StockQuarantineSearchResponseSchema
>;
export type StockQuarantineFormModel = z.infer<
  typeof StockQuarantineFormSchema
>;

//Stock Levels
export type StockLevelSearchModel = z.infer<typeof StockLevelSearchSchema>;

export type StockLevelOtherQuantitiesModel = z.infer<
  typeof StockLevelOtherQuantitiesSchema
>;

export type StockLevelSearchRequestModel = z.infer<
  typeof StockLevelSearchRequestSchema
>;

export type StockInternalProductSearchRequestModel = z.infer<
  typeof StockInternalProductSearchRequestSchema
>;

export type StockInternalProductModel = z.infer<
  typeof StockInternalProductSchema
>;

export type StockInternalProductResponseModel = z.infer<
  typeof StockInternalProductResponseSchema
>;

export type StockLevelSearchResponseModel = z.infer<
  typeof StockLevelSearchResponseSchema
>;

export type StockLevelGetModel = z.infer<typeof StockLevelGetSchema>;

export type StockLevelByIdModel = z.infer<typeof StockLevelByIdSchema>;

// Manage Stock Levels

export type CreateJobFromStockFormItemModel = z.infer<
  typeof CreateJobFromStockFormItemSchema
>;

export type CreateJobFromStockFormModel = z.infer<
  typeof CreateJobFromStockFormSchema
>;
export type CreateJobFromInternalStockModel = z.infer<
  typeof CreateJobFromInternalStockSchema
>;
export type CreateJobFromInternalStockRequestModel = z.infer<
  typeof CreateJobFromInternalStockRequestSchema
>;
export type CreateJobFromInternalStockResponseModel = z.infer<
  typeof CreateJobFromInternalStockResponseSchema
>;

export type GetStockAssociatedOrdersRequestModel = z.infer<
  typeof GetStockAssociatedOrdersRequestSchema
>;
export type GetStockLevelTotalsByLocationIdRequestModel = z.infer<
  typeof GetStockLevelTotalsByLocationIdRequestSchema
>;
export type StockAssociatedOrderModel = z.infer<
  typeof StockAssociatedOrderSchema
>;
export type StockLevelQuantityModel = z.infer<typeof StockLevelQuantitySchema>;
export type GetStockAssociatedOrdersResponseModel = z.infer<
  typeof GetStockAssociatedOrdersResponseSchema
>;
export type GetStockLevelTotalsByLocationIdResponseModel = z.infer<
  typeof GetStockLevelTotalsByLocationIdResponseSchema
>;

export type StockMeasurementsModel = z.infer<typeof StockMeasurementsSchema>;
export type AddStockMeasurementsModel = z.infer<
  typeof AddStockMeasurementsSchema
>;
export type SearchMeasurementsBySkuRequestModel = z.infer<
  typeof SearchMeasurementsBySkuRequestSchema
>;
export type SearchMeasurementsBySkuResponseModel = z.infer<
  typeof SearchMeasurementsBySkuResponseSchema
>;

export type GetByServiceLocationOrStockRequestModel = z.infer<
  typeof GetByServiceLocationOrStockRequestSchema
>;
export type GetByServiceLocationOrStockResponseModel = z.infer<
  typeof GetByServiceLocationOrStockResponseSchema
>;

export type StockTakeProfileCycleDaysTypeModel = z.infer<
  typeof StockTakeProfileCycleDaysTypeSchema
>;
export type GetStockReturnResponseModel = z.infer<
  typeof GetStockReturnResponseSchema
>;
export type StockTakeItemIdModel = z.infer<typeof StockTakeItemIdSchema>;
export type StockTakeIdModel = z.infer<typeof StockTakeIdSchema>;
export type StockTakeItemModel = z.infer<typeof StockTakeItemSchema>;
export type StockReturnIdModel = z.infer<typeof StockReturnIdSchema>;
export type StockReturnFormModel = z.infer<typeof StockReturnFormSchema>;
export type StockReturnIdsModel = z.infer<typeof StockReturnIdsSchema>;
